import { ethers } from 'ethers';
import type { RuntimeConfig } from 'nuxt/schema';
import type { BlockchainConfig } from '~/types/contractAddresses';
import { HalloweenItem, type ItemDescription } from '~/types/halloween/items';

class HalloweenItemRegistry {
  #registry = new Map<HalloweenItem, ItemDescription>();
  #mainnetAddresses = false;
  #initCallback: Function | null = null;

  public setup(config: RuntimeConfig, blockchain: BlockchainConfig) {
    if (config.public.env === 'prod') this.#mainnetAddresses = true;

    this.register(HalloweenItem.Axe, {
      name: 'halloweenItemAxe',
      address: this.select('0x62dB5C9911611699709f5C10D449510145de78cF', '0xb28213f75f97f7ff2b5FfF58ee5970dcB8CD85D8')
    });
    this.register(HalloweenItem.Torch, {
      name: 'halloweenItemTorch',
      address: this.select('0x79fC5fa9f1911DbabAB6020310184C04DDc27501', '0xd278747ffEd23bF512795Fe4F4C75F7F2b148a44')
    });
    this.register(HalloweenItem.Bat, {
      name: 'halloweenItemBat',
      address: this.select('0xD47240e05D1382Cf8fb1dd4206FD9cBc934d8354', '0x73B9216f5C5DC5197BcB703652feDA436C73BB05')
    });
    this.register(HalloweenItem.Bones, {
      name: 'halloweenItemBones',
      address: this.select('0x3Bad78C170870A5021e106f27897f2B12E93ad49', '0x11023ea4EDD548F910a5B0362a738021303A3851')
    });
    this.register(HalloweenItem.Bug, {
      name: 'halloweenItemBug',
      address: this.select('0x5906e72eAF3ce8CC878A1ED57dcb3Cd916C73142', '0x1d59c4e05ce270f3954FF4CEE541396e1dF2035F')
    });
    this.register(HalloweenItem.Flowers, {
      name: 'halloweenItemFlowers',
      address: this.select('0x0D45ce1033e2721B4fDA2daE4429f1e911f5b17F', '0x2D76A34A2501F02c4116ecF1a7fD1b43Fe8E33f4')
    });
    this.register(HalloweenItem.Mahogany, {
      name: 'halloweenItemMahogany',
      address: this.select('0x39C0b17fa60362862E4113c04FAb16E315463BdA', '0xFfFd94DE39f2DBD770535acd6Ca76aeB0bEEf1B2')
    });
    this.register(HalloweenItem.Minerals, {
      name: 'halloweenItemMinerals',
      address: this.select('0x6610A46CB055818507A7F73700fc65769a293233', '0xA2b28ee40958eC4d02468Aa445e27B850A24983C')
    });
    this.register(HalloweenItem.Mushrooms, {
      name: 'halloweenItemMushrooms',
      address: this.select('0x9BfEB7dF4F1E43EE078E4F89BB07ab82F4AA5311', '0xd9a7736Fe52525E76b1aB2231617ea3B9d05ce33')
    });
    this.register(HalloweenItem.Roots, {
      name: 'halloweenItemRoots',
      address: this.select('0x7E6C55B6515bE3BCd55d4f094F8872956B3e19e0', '0xaC67Da417244358C0c6a5cF526d6Cea58caEc4e9')
    });
    this.register(HalloweenItem.PirateHead, {
      name: 'Pirate Head',
      address: ethers.ZeroAddress
    });
    this.register(HalloweenItem.Heart, {
      name: 'Heart',
      address: ethers.ZeroAddress
    });
    this.register(HalloweenItem.DeadWater, {
      name: 'halloweenItemDeadWater',
      address: this.select('0x3881D32B598b17A67Ea9fc73F477a555b0aEc29a', '0x54349246784D45E62b6FD03A87B43B1106BBf9fF')
    });
    this.register(HalloweenItem.Resin, {
      name: 'halloweenItemResin',
      address: this.select('0x9185C93669b39C494E21F3C0fe7b9A1F7Ad17eb1', '0x0Df36C7d279A58E44605faCaD8823aF3f84ACF40')
    });
    this.register(HalloweenItem.Rope, {
      name: 'halloweenItemRope',
      address: this.select('0x8b266B7474839A749151b1Ff1Fdf897368a7C485', '0xa0c1b18CD6c16516fe235cd858bEdc516e41d65b')
    });
    this.register(HalloweenItem.Potion, {
      name: 'halloweenItemPotion',
      address: this.select('0x379A2bc0f67C6AA74BC1b9dd5F91aDc0Ce41B365', '0xb869eE1703516Fc3520B4731f25cc2176eD6843E')
    });
    this.register(HalloweenItem.Antidote, {
      name: 'halloweenItemAntidote',
      address: this.select('0x656381B67a39893a6C078bd43FD57Bae822A93d0', '0x46f49c37930Cc3b04e6900b5992078ebB9210609')
    });
    this.register(HalloweenItem.Leaf, {
      name: 'coreLeave',
      address: blockchain.contracts.leaf.addresses.contract
    });

    this.register(HalloweenItem.Pearl, {
      name: 'corePearl',
      address: blockchain.contracts.pearl.addresses.contract
    });
    this.register(HalloweenItem.Gold, {
      name: 'coreGold',
      address: blockchain.contracts.gold.addresses.contract
    });
    this.register(HalloweenItem.GoldOre, {
      name: 'coreGoldOre',
      address: blockchain.contracts.goldOre.addresses.contract
    });

    this.#initCallback?.();
  }

  public onInit(callback: Function | null) {
    this.#initCallback = callback;
  }

  public register(key: HalloweenItem, data: Omit<ItemDescription, 'id'>) {
    this.#registry.set(key, {
      id: key,
      ...data
    });
  }

  public get(key: HalloweenItem | ItemDescription) {
    return this.#registry.get(typeof key === 'string' ? key : key.id)!;
  }

  public getByAddress(address: string) {
    for (const value of this.#registry.values()) {
      if (value.address.toLowerCase() === address.toLowerCase()) {
        return value;
      }
    }

    return null;
  }

  public resolveIcon(key: HalloweenItem | string | ItemDescription) {
    const _id = typeof key === 'string' ? key : key.id;

    return `/img/halloween/items/${_id}.png`;
  }

  private select(addressA: string, addressB: string) {
    if (this.#mainnetAddresses) {
      return addressA;
    } else {
      return addressB;
    }
  }

  public getAllTokens(excludeZero = false) {
    const list = Array.from(this.#registry.values());

    if (!excludeZero) return list;
    return list.filter((item) => item.address !== ethers.ZeroAddress);
  }

  public getLocale(key: HalloweenItem) {
    return this.#registry.get(key)!.name;
  }

  public withQuantity(item: HalloweenItem | ItemDescription, quantity: number) {
    return {
      ...this.get(item),
      quantity
    };
  }
}

const halloweenItemRegistry = new HalloweenItemRegistry();
export default halloweenItemRegistry;
